"use client";

import { useGetV2WireDetails } from "@/gen/orval/webplatform/endpoints/wire-details/wire-details.gen";
import { onlyShowLast4 } from "@/lib/utils/mask";

import type { BankAccountsTypeExpanded, BankAccountType } from "@/subsystems/transfer/bank-accounts/types";

/**
 * Soft-deleted bank accounts should stay out of tables and selectors.
 * We only filter explicit delete states here so non-deleted records remain visible.
 */
const DELETED_STATUSES: ReadonlySet<string> = new Set(["deleted", "pending deletion"]);

function isActiveBankAccount(account: BankAccountType): boolean {
  return !account.status || !DELETED_STATUSES.has(account.status);
}

export function useGetAllBankAccountNicknames() {
  const { bankAccountsEncoded } = useGetAllBankAccountsEncoded();

  if (bankAccountsEncoded.isLoading || !bankAccountsEncoded.data) {
    return {
      bankAccountNicknames: null,
      isLoading: bankAccountsEncoded.isLoading,
    };
  }

  const bankAccountNicknames = bankAccountsEncoded.data
    .filter(isActiveBankAccount)
    .map((bankAccount) => bankAccount?.nickname)
    .filter((nickname): nickname is string => Boolean(nickname));

  return {
    bankAccountNicknames,
    isLoading: bankAccountsEncoded.isLoading,
  };
}

export function useGetAllBankAccounts() {
  const { bankAccountsEncoded } = useGetAllBankAccountsEncoded();
  const { bankAccountsDecoded } = useGetAllBankAccountsDecoded();

  const decodedLoaded = bankAccountsDecoded?.isSuccess;
  const bankAccountsToProcess = decodedLoaded ? bankAccountsDecoded : bankAccountsEncoded;

  const processedData: BankAccountsTypeExpanded[] = processResults(bankAccountsToProcess.data);

  return {
    bankAccounts: { ...bankAccountsToProcess, data: processedData },
    bankAccountsAreDecoded: decodedLoaded,
    bankAccountsAreEncoded: !decodedLoaded,
  };
}

function processResults(data?: BankAccountType[]): BankAccountsTypeExpanded[] {
  return (
    data?.filter(isActiveBankAccount).map((bankAccount) => ({
      ...bankAccount,
      accountNumberMasked: bankAccount?.accountNumber ? onlyShowLast4(bankAccount.accountNumber) : null,
      routingNumberMasked: bankAccount?.routingNumber ? onlyShowLast4(bankAccount.routingNumber) : null,
    })) ?? []
  );
}

export function useGetSingleBankAccount({ pkFilter }: { pkFilter: string }) {
  const { bankAccounts, bankAccountsAreDecoded, bankAccountsAreEncoded } = useGetAllBankAccounts();

  const filteredBankAccount = bankAccounts?.data?.filter((account) => pkFilter === account.pk)?.[0];

  return {
    allBankAccounts: bankAccounts,
    bankAccount: { ...bankAccounts, data: filteredBankAccount },
    bankAccountIsDecoded: bankAccountsAreDecoded,
    bankAccountIsEncoded: bankAccountsAreEncoded,
  };
}

function useGetAllBankAccountsDecoded() {
  return {
    bankAccountsDecoded: useGetV2WireDetails(),
  };
}

function useGetAllBankAccountsEncoded() {
  return {
    bankAccountsEncoded: useGetV2WireDetails(),
  };
}
